import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import './chat-launcher';
import './chat-header';
import './chat-messages';
import './chat-input';
import './suggestion-chips';
import './report-picker';
import './loading-screen';
import {
  AiApiService,
  SessionExpiredError,
  RateLimitedError,
  ApiError,
  NetworkError,
} from '../services/ai-api.service';
import { ReportsApiService } from '../services/reports-api.service';
import {
  LaravelClient,
  SessionStartError,
  type AuthTokenProvider,
} from '../services/laravel-client.service';
import { ChatStore } from '../state/chat-store';
import { parseTheme } from '../utils/parse-theme';
import { DEFAULT_TOKENS } from '../styles/tokens';
import { ensureFontsLoaded } from '../utils/fonts';
import { buildDrillDownQuestion, type DrillDownContext } from '../utils/drill-down-template';
import { isCacheOwnedBy, sessionOwnerFingerprint } from '../utils/session-owner';
import type { ReportListItem } from '../types/report';
import type { LaravelReport, LaravelSessionResponse } from '../types/laravel';

type Phase =
  | 'closed'
  | 'loading-reports'
  | 'picking-report'
  | 'starting-session'
  | 'ready'
  | 'error';

const SESSION_STORAGE_PREFIX = 'bia-session::';

/** localStorage key for the user's persisted floating/docked preference. */
const DISPLAY_MODE_KEY = 'bia:display-mode';

@customElement('bi-explorer-ai-chat')
export class BiExplorerAiChat extends LitElement {
  static styles = css`
    :host {
      position: fixed;
      right: 24px;
      bottom: 24px;
      /* Sit above third-party support/chat widgets (Intercom, Zendesk, Crisp,
         tawk.to…) which use very high z-indexes — the old 99999 lost to them,
         leaving the support button floating over the open BIA panel. Default
         to the 32-bit max so BIA wins by default; a host can tune it down via
         --bia-z-index if its own layering needs BIA lower. The chart-maximize
         <dialog> uses the browser top layer, which is above z-index entirely,
         so this doesn't affect it. */
      z-index: var(--bia-z-index, 2147483647);
      font-family: var(--bia-font-family, sans-serif);
      color: var(--bia-text, #0f0f0e);
      /* Reset inheritable host styles that may bleed in from parents
         (e.g. Quasar's q-toolbar-title sets white-space: nowrap which
         inherits through shadow DOM and breaks all wrapping inside). */
      white-space: normal;
      word-break: normal;
      word-spacing: normal;
      line-height: normal;
      text-align: left;
      direction: ltr;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-rendering: optimizeLegibility;
    }
    .panel {
      position: absolute;
      right: 0;
      bottom: 80px;
      width: 384px;
      max-width: calc(100vw - 48px);
      height: 600px;
      max-height: calc(100vh - 120px);
      background: var(--bia-background, #fff);
      border: 1px solid var(--bia-border, #e8e5dc);
      border-radius: var(--bia-border-radius, 14px);
      box-shadow: var(--bia-shadow-panel, 0 24px 60px -20px rgba(15, 15, 14, 0.18));
      display: flex;
      flex-direction: column;
      overflow: hidden;
      /* IMPORTANT: never apply transform/filter here. Anything that creates
         a "containing block" hijacks position:fixed descendants (chart
         maximize, etc.) and traps them inside the panel. Animate opacity
         only — no transform. */
      animation: panel-in 280ms ease-out both;
    }
    @keyframes panel-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    .panel.max {
      position: fixed;
      top: 4vh;
      left: 4vw;
      right: 4vw;
      bottom: 4vh;
      width: auto;
      height: auto;
      max-width: 1280px;
      max-height: 880px;
      margin: 0 auto;
      box-shadow:
        0 60px 120px -30px rgba(15, 15, 14, 0.32),
        0 16px 40px -12px rgba(15, 15, 14, 0.18);
      transform-origin: center;
    }
    /* hide-launcher: the host renders its own trigger, so there's no built-in
       button below the panel. Without this, the floating card keeps the 80px
       gap it normally leaves for the launcher and appears to hover mid-air
       ("voando"). The flush class is added in render() from the hideLauncher
       PROPERTY (not the reflected attribute — Angular hosts may not reflect it
       in time), so this reliably anchors the floating panel to the bottom —
       a clean 24px inset that mirrors the host's right offset. */
    .panel.flush {
      bottom: 0;
      /* Anchored to the bottom, so it can use more vertical space than the
         classic floating panel. Taller default; tunable per host via
         --bia-panel-height (e.g. set it to 88vh on the host to go nearly
         full-height). Capped 24px from the top edge so it never touches it. */
      height: var(--bia-panel-height, 760px);
      max-height: calc(100vh - 48px);
    }
    /* Docked mode — full-height side panel flush to the right edge, sitting
       BESIDE the report (the host shrinks the report via --bia-dock-occupied)
       instead of floating over it. Maximize still works (centered overlay). */
    :host([display-mode='docked']) .panel:not(.max) {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      width: var(--bia-dock-width, 420px);
      max-width: 96vw;
      height: 100vh;
      max-height: 100vh;
      border-radius: 0;
      border: none;
      border-left: 1px solid var(--bia-border, #e8e5dc);
      box-shadow: var(--bia-shadow-panel, -8px 0 40px -20px rgba(15, 15, 14, 0.18));
    }
    bia-chat-messages {
      flex: 1 1 0;
      min-width: 0;
      min-height: 0;
      width: 100%;
    }
    bia-chat-header,
    bia-chat-input,
    bia-loading-screen,
    bia-report-picker {
      flex-shrink: 0;
      min-width: 0;
      width: 100%;
    }
    bia-loading-screen,
    bia-report-picker {
      flex: 1 1 0;
      min-height: 0;
    }
    .suggestions {
      padding: 0 16px 4px;
      min-width: 0;
      width: 100%;
      box-sizing: border-box;
      background: var(--bia-background, #fff);
    }
  `;

  // ---------- Push mode (legacy / dev harness) ----------
  @property({ attribute: 'session-token', type: String }) sessionToken = '';
  @property({ attribute: 'api-url', type: String }) apiUrl = '';
  @property({ attribute: 'reports-api-url', type: String }) reportsApiUrl = '';
  @property({ attribute: 'report-id', type: String }) reportId = '';
  @property({ attribute: 'workspace-id', type: String }) workspaceId = '';
  @property({ attribute: 'dataset-id', type: String }) datasetId = '';

  // ---------- Fetch mode (production with Laravel) ----------
  @property({ attribute: 'laravel-reports-url', type: String }) laravelReportsUrl = '';
  @property({ attribute: 'laravel-session-url', type: String }) laravelSessionUrl = '';
  @property({ attribute: 'current-report-id', type: String }) currentReportId = '';
  @property({ attribute: 'current-report-name', type: String }) currentReportName = '';
  @property({ attribute: 'user-name', type: String }) userName = '';

  /**
   * Function returning the current host JWT. Set via JS property
   * (NOT an HTML attribute) so token rotation is transparent.
   */
  authTokenProvider: AuthTokenProvider | null = null;

  // ---------- Common ----------
  @property({ attribute: 'theme', type: String }) theme = '';
  @property({ attribute: 'lang', type: String }) override lang = 'pt-BR';
  /** How the open panel is laid out:
   *  - 'floating' (default): overlay card bottom-right — the current behavior.
   *    Quasar hosts keep this, so existing clients see no change.
   *  - 'docked': full-height side panel flush to the right edge. The host
   *    makes room by reading `--bia-dock-occupied` (set on <html> to the dock
   *    width while open, 0 while closed) — e.g. `padding-right:
   *    var(--bia-dock-occupied, 0)` on the report container. Dock width is
   *    `--bia-dock-width` (default 420px). */
  @property({ attribute: 'display-mode', type: String, reflect: true })
  displayMode: 'floating' | 'docked' = 'floating';
  /** When true, the header shows a button to let the USER switch between
   *  floating and docked at runtime. Off by default so legacy Quasar hosts
   *  (floating only) are unaffected — the Angular host opts in. */
  @property({ attribute: 'dock-toggle', type: Boolean }) dockToggle = false;
  /** When true, the built-in floating launcher button is NOT rendered. The
   *  host provides its own trigger (e.g. a header/menu button) and opens the
   *  chat via the public `openChat()` / `toggleChat()` methods. Off by default
   *  so legacy Quasar hosts keep the floating button unchanged.
   *  ``reflect: true`` keeps the attribute in the DOM even when the host sets
   *  the property via JS, so the ``:host([hide-launcher])`` style below (which
   *  anchors the floating panel to the bottom, since there's no launcher to
   *  leave room for) always applies. */
  @property({ attribute: 'hide-launcher', type: Boolean, reflect: true })
  hideLauncher = false;
  /** When true, the user's floating/docked choice (from the `dock-toggle`
   *  button) is persisted to localStorage and restored on load — so it sticks
   *  across reloads without a server round-trip. localStorage wins over the
   *  `display-mode` attribute when present. Off by default. */
  @property({ attribute: 'persist-display-mode', type: Boolean })
  persistDisplayMode = false;

  @state() private open = false;
  @state() private maximized = false;
  @state() private phase: Phase = 'closed';
  @state() private phaseError = '';
  @state() private reports: LaravelReport[] = [];
  @state() private legacyReports: ReportListItem[] = [];
  @state() private prefill = '';
  @state() private activeReportName = '';
  /**
   * Internal Laravel UUID of the report whose session is currently active.
   * Used to detect when the user is switching to a different report — when
   * that happens, we must wipe the chat history because the dataset context
   * is different. Tracked separately from `reportId` (the Power BI report id)
   * because the picker hands us the platform token, not the PBI id.
   */
  @state() private activeReportToken = '';

  private store = new ChatStore();
  private aiApi: AiApiService | null = null;
  private legacyReportsApi: ReportsApiService | null = null;
  private laravel: LaravelClient | null = null;

  // -----------------------------------------------------------
  // Lifecycle
  // -----------------------------------------------------------

  connectedCallback(): void {
    super.connectedCallback();
    // Restore the user's last floating/docked choice (localStorage wins over
    // the display-mode attribute when persistence is on).
    if (this.persistDisplayMode) {
      const stored = this.readStoredDisplayMode();
      if (stored) this.displayMode = stored;
    }
    ensureFontsLoaded();
    this.applyTokens();
    this.store.addEventListener('change', this.onStoreChange);
    this.addEventListener('chartpointclick', this.onChartPointClick);
    if (this.isPushMode()) {
      this.refreshPushServices();
    }
    // Fetch mode: do nothing here. Lazy boot — wait for openPanel().
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.store.removeEventListener('change', this.onStoreChange);
    this.removeEventListener('chartpointclick', this.onChartPointClick);
    // Don't leave the host padded if we unmount while docked + open.
    // `documentElement` can already be null when the whole document is
    // being torn down (page unload, test teardown), and throwing from
    // disconnectedCallback surfaces as an unhandled error rather than
    // anything actionable — the padding is moot at that point anyway.
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.style.setProperty('--bia-dock-occupied', '0px');
    }
  }

  private onStoreChange = (): void => {
    this.requestUpdate();
  };

  private applyTokens(): void {
    // Only seed defaults for tokens the host hasn't already defined upstream
    // (e.g. via `:root` CSS or document.documentElement.style.setProperty).
    // Setting them unconditionally would create an inline style on this host
    // element, which has higher cascade priority than inherited values from
    // <html>, blocking the host's whitelabel theme.
    const computed = getComputedStyle(this);
    for (const [key, value] of Object.entries(DEFAULT_TOKENS)) {
      const inherited = computed.getPropertyValue(key).trim();
      if (!inherited) {
        this.style.setProperty(key, value);
      }
    }
    // The `theme` attribute is an explicit per-instance override, so it
    // always wins over both defaults and host-level vars.
    for (const [key, value] of Object.entries(parseTheme(this.theme))) {
      this.style.setProperty(key, value);
    }
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('theme')) this.applyTokens();
    if (this.isPushMode() && (changed.has('sessionToken') || changed.has('apiUrl') || changed.has('reportsApiUrl'))) {
      this.refreshPushServices();
    }
    if (changed.has('reportId') && this.isPushMode() && this.open) {
      this.store.clear();
    }
    if (changed.has('currentReportId') && this.isFetchMode()) {
      // Host changed the report context — may happen while chat is open,
      // closed, or anywhere in between. startSessionFor handles the history
      // wipe based on whether the token really changed.
      //
      // IMPORTANT: we deliberately do NOT reset activeReportToken when the
      // report goes empty (user moved off a report). Keeping it means that
      // opening a DIFFERENT report next still registers as a switch and wipes
      // the previous conversation — even on the path report A → no report →
      // report B (close one, open another). The picker shows based on
      // currentReportId being empty, not on activeReportToken; and reopening
      // the SAME report resumes it (token matches → no wipe).
      if (this.open && this.currentReportId) {
        this.handleReportContextChange();
      }
    }
  }

  // -----------------------------------------------------------
  // Mode detection
  // -----------------------------------------------------------

  private isFetchMode(): boolean {
    return !!this.laravelSessionUrl;
  }

  private isPushMode(): boolean {
    return !this.isFetchMode() && !!this.sessionToken && !!this.apiUrl;
  }

  // -----------------------------------------------------------
  // Push mode wiring (legacy — preserved for dev harness)
  // -----------------------------------------------------------

  private refreshPushServices(): void {
    if (!this.sessionToken || !this.apiUrl) {
      this.aiApi = null;
      this.legacyReportsApi = null;
      return;
    }
    this.aiApi = new AiApiService({
      apiUrl: this.apiUrl,
      sessionToken: this.sessionToken,
    });
    if (this.reportsApiUrl) {
      this.legacyReportsApi = new ReportsApiService({
        apiUrl: this.reportsApiUrl,
        sessionToken: this.sessionToken,
      });
    }
  }

  // -----------------------------------------------------------
  // Fetch mode wiring (production)
  // -----------------------------------------------------------

  private ensureLaravelClient(): LaravelClient {
    if (!this.laravel) {
      if (!this.authTokenProvider) {
        throw new Error(
          'authTokenProvider is required in fetch mode. Set it as a JS property on <bi-explorer-ai-chat> after mounting.',
        );
      }
      this.laravel = new LaravelClient({
        reportsUrl: this.laravelReportsUrl,
        sessionStartUrl: this.laravelSessionUrl,
        getAuthToken: this.authTokenProvider,
      });
    }
    return this.laravel;
  }

  private storageKey(reportToken: string): string {
    return `${SESSION_STORAGE_PREFIX}${this.laravelSessionUrl}::${reportToken}`;
  }

  /**
   * Identity of whoever is logged in right now, for cache ownership.
   *
   * Empty when there is no token — and an empty owner never matches a
   * cache entry, so the session is re-minted instead of guessed.
   */
  private async currentSessionOwner(): Promise<string> {
    try {
      const token = await this.authTokenProvider?.();
      return sessionOwnerFingerprint(token ?? '');
    } catch {
      return '';
    }
  }

  /**
   * Restore a cached session — ONLY for the user who minted it.
   *
   * `sessionStorage` is per-tab and survives a logout/login in that tab.
   * Without the ownership check, a second user in the same tab restored
   * the first user's token and queried Power BI as them, answering over
   * data they must not see (incident 2026-09-09: a master's session was
   * reused by a freshly created user, who then saw the whole report).
   */
  private async restoreCachedSession(reportToken: string): Promise<boolean> {
    try {
      const raw = sessionStorage.getItem(this.storageKey(reportToken));
      if (!raw) return false;
      const cached = JSON.parse(raw) as LaravelSessionResponse & {
        cachedAt: number;
        owner?: string;
      };
      if (!isCacheOwnedBy(cached, await this.currentSessionOwner())) {
        // Another user's session, or one minted before this check
        // existed. Drop it and let Laravel mint a fresh one for whoever
        // is actually logged in.
        sessionStorage.removeItem(this.storageKey(reportToken));
        return false;
      }
      // expires_at is ISO; bail out if expired
      if (new Date(cached.expires_at).getTime() <= Date.now()) {
        sessionStorage.removeItem(this.storageKey(reportToken));
        return false;
      }
      this.applyLaravelSession(cached);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Cache a session, stamped with who it belongs to.
   *
   * Without the stamp the entry is unrestorable (see `isCacheOwnedBy`),
   * so a failure to resolve the owner degrades to "never cached" — one
   * extra call to Laravel per turn, never another user's session.
   */
  private async cacheSession(
    reportToken: string,
    session: LaravelSessionResponse,
  ): Promise<void> {
    try {
      const owner = await this.currentSessionOwner();
      sessionStorage.setItem(
        this.storageKey(reportToken),
        JSON.stringify({ ...session, cachedAt: Date.now(), owner }),
      );
    } catch {
      // sessionStorage may be unavailable (private mode, SSR, etc) — ignore
    }
  }

  private applyLaravelSession(session: LaravelSessionResponse): void {
    this.aiApi = new AiApiService({
      apiUrl: session.ai_api_base,
      sessionToken: session.session_token,
    });
    this.reportId = session.report_id;
    this.workspaceId = session.workspace_id;
    this.datasetId = session.dataset_id;
    this.activeReportName = session.report_name ?? this.currentReportName ?? '';
    this.phase = 'ready';
  }

  private async startSessionFor(reportToken: string, displayName?: string): Promise<void> {
    // Switching reports: wipe the previous conversation so messages from
    // report A don't bleed into a fresh session for report B. If the user
    // picks the SAME report they were already talking to (activeReportToken
    // matches), we keep the history — they're resuming.
    const isSwitchingReport =
      !!this.activeReportToken && this.activeReportToken !== reportToken;
    if (isSwitchingReport) {
      this.store.clear();
      this.aiApi = null;
      this.dispatchEvent(
        new CustomEvent('reportChanged', {
          detail: { fromToken: this.activeReportToken, toToken: reportToken },
          bubbles: true,
          composed: true,
        }),
      );
    }
    this.activeReportToken = reportToken;

    this.phase = 'starting-session';
    this.phaseError = '';
    if (displayName) this.activeReportName = displayName;

    if (await this.restoreCachedSession(reportToken)) {
      return;
    }

    try {
      const client = this.ensureLaravelClient();
      const session = await client.startSession(reportToken);
      this.applyLaravelSession(session);
      await this.cacheSession(reportToken, session);
    } catch (err) {
      this.phase = 'error';
      this.phaseError =
        err instanceof SessionStartError
          ? err.message
          : err instanceof NetworkError
            ? 'Erro de conexão. Verifique sua rede.'
            : err instanceof SessionExpiredError
              ? 'Sua sessão expirou. Recarregue a página.'
              : err instanceof Error
                ? err.message
                : 'Erro ao iniciar o chat.';
    }
  }

  private async loadReportsList(): Promise<void> {
    this.phase = 'loading-reports';
    this.phaseError = '';
    try {
      const client = this.ensureLaravelClient();
      this.reports = await client.listReports();
      this.phase = 'picking-report';
    } catch (err) {
      this.phase = 'error';
      this.phaseError =
        err instanceof NetworkError
          ? 'Erro de conexão. Verifique sua rede.'
          : err instanceof SessionExpiredError
            ? 'Sua sessão expirou. Recarregue a página.'
            : err instanceof Error
              ? err.message
              : 'Erro ao carregar relatórios.';
    }
  }

  private handleReportContextChange(): void {
    // Host changed current-report-id from the outside (Quasar router nav,
    // attribute update, etc.). `startSessionFor` below handles the history
    // wipe if the new report token is different from the current one, so
    // we don't need to clear here.
    if (this.currentReportId) {
      void this.startSessionFor(this.currentReportId, this.currentReportName);
    } else {
      // Moving to a no-report context — force user to pick again. Clear
      // since there's no new report to match against.
      this.store.clear();
      this.aiApi = null;
      this.activeReportToken = '';
      void this.loadReportsList();
    }
  }

  // -----------------------------------------------------------
  // Panel open/close
  // -----------------------------------------------------------

  /** In docked mode, publish how much horizontal space the dock occupies so
   *  the host can shrink the report: `--bia-dock-occupied` on <html> = the
   *  dock width while open, 0 while closed (floating mode never sets it). */
  private updateDockOccupancy(): void {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    // Maximized = centered overlay (on top), NOT docked beside the report —
    // so release the reserved space while maximized to avoid a phantom gap.
    const occupied =
      this.displayMode === 'docked' && this.open && !this.maximized
        ? 'var(--bia-dock-width, 420px)'
        : '0px';
    root.style.setProperty('--bia-dock-occupied', occupied);
  }

  private async openPanel(): Promise<void> {
    this.open = true;
    this.updateDockOccupancy();
    this.dispatchEvent(new CustomEvent('chatOpened', { bubbles: true, composed: true }));

    if (this.isFetchMode()) {
      if (this.currentReportId) {
        await this.startSessionFor(this.currentReportId, this.currentReportName);
      } else {
        await this.loadReportsList();
      }
      return;
    }

    // Push mode — try to load the legacy report list if configured
    if (this.legacyReportsApi && !this.legacyReports.length) {
      try {
        const list = await this.legacyReportsApi.list();
        this.legacyReports = Array.isArray(list) ? list : [];
      } catch (err) {
        this.handleError(err);
      }
    }
  }

  private closePanel(): void {
    this.open = false;
    this.maximized = false;
    this.updateDockOccupancy();
    this.dispatchEvent(new CustomEvent('chatClosed', { bubbles: true, composed: true }));
  }

  private toggleMaximize(): void {
    this.maximized = !this.maximized;
    this.updateDockOccupancy();
  }

  /** Read a valid persisted display mode from localStorage, or null. */
  private readStoredDisplayMode(): 'floating' | 'docked' | null {
    try {
      const v = window.localStorage?.getItem(DISPLAY_MODE_KEY);
      return v === 'floating' || v === 'docked' ? v : null;
    } catch {
      return null;
    }
  }

  /** User toggle (when `dock-toggle` is enabled): flip floating <-> docked.
   *  Persists the choice to localStorage (when enabled) so it sticks across
   *  reloads. */
  private toggleDock(): void {
    this.displayMode = this.displayMode === 'docked' ? 'floating' : 'docked';
    this.maximized = false;
    this.updateDockOccupancy();
    if (this.persistDisplayMode) {
      try {
        window.localStorage?.setItem(DISPLAY_MODE_KEY, this.displayMode);
      } catch {
        /* private mode / quota — ignore, the in-memory state still works */
      }
    }
  }

  // ---------------------------------------------------------------
  // Public API — for hosts using `hide-launcher` with their own trigger
  // (e.g. a header/menu button). Call on the element: `el.openChat()`.
  // ---------------------------------------------------------------

  /** Open the chat panel (idempotent). */
  openChat(): void {
    if (!this.open) void this.openPanel();
  }

  /** Close the chat panel (idempotent). */
  closeChat(): void {
    if (this.open) this.closePanel();
  }

  /** Toggle the chat panel open/closed. */
  toggleChat(): void {
    if (this.open) {
      this.closePanel();
    } else {
      void this.openPanel();
    }
  }

  /**
   * Handles a bubbled `chartpointclick` event from any chart-renderer
   * inside the widget. Builds the drill-down question via the template
   * helper and sets `this.prefill` — `<bia-chat-input>` observes this
   * and populates its textarea (and focuses it, per Task 4).
   */
  private onChartPointClick = (ev: Event): void => {
    const detail = (ev as CustomEvent<DrillDownContext | null>).detail;
    if (!detail) return;
    this.prefill = buildDrillDownQuestion(detail);
  };

  /**
   * Handle the refresh click from the header. Does two things so a single
   * user action fixes the two cache layers that go stale independently:
   *
   *   1. **Schema cache** (AI API side, keyed by parceiro_id) — evicted
   *      via POST /chat/schema/refresh so the next chat message cold-paths
   *      Power BI introspection. Useful when the semantic model was
   *      updated (new measure, renamed column, etc.).
   *
   *   2. **Session cache** (Redis + widget sessionStorage) — the session
   *      holds a snapshot of the openai_key from the moment Laravel minted
   *      it. If the admin rotated the key, the cached session keeps
   *      failing with AuthenticationError. Clearing sessionStorage and
   *      re-calling startSessionFor forces Laravel to mint a fresh
   *      session with whatever key is now current.
   *
   * The header button shows a spinner while both steps run and flips to
   * a brief "ok" checkmark on success.
   */
  private onRefreshSchema = async (ev: Event): Promise<void> => {
    const headerEl = ev.currentTarget as
      | (HTMLElement & {
          markRefreshDone?: () => void;
          markRefreshFailed?: () => void;
        })
      | null;

    // Fetch mode: session re-mint is the primary recovery path and
    // MUST run first. If we called refreshSchema on the (possibly dead)
    // old session, the 401 would throw and we'd skip the re-mint
    // entirely — which was the bug where clicking refresh appeared to
    // do nothing. Push mode has no session to re-mint.
    if (this.isFetchMode() && this.reportId) {
      const reportToken = this.findReportTokenById(this.reportId);
      if (reportToken) {
        try {
          sessionStorage.removeItem(this.storageKey(reportToken));
        } catch {
          // sessionStorage may be unavailable (private mode, SSR) — ignore
        }
        try {
          await this.startSessionFor(reportToken, this.activeReportName);
        } catch (err) {
          console.warn('[bia] re-mint failed', err);
          headerEl?.markRefreshFailed?.();
          this.store.addBubble({
            role: 'error',
            content:
              'Não foi possível atualizar a conexão. Tente novamente '
              + 'em instantes ou contate o administrador do sistema.',
          });
          return;
        }
      }
    }

    // Best-effort schema eviction on the (now fresh) session. If this
    // fails the user still gets a working chat — the schema cache will
    // simply rebuild on the next cold-path query.
    if (this.aiApi) {
      try {
        await this.aiApi.refreshSchema();
      } catch (err) {
        console.warn('[bia] schema refresh failed', err);
      }
    }

    // Visible confirmation in the chat so the user knows the action took
    // effect and can safely retry their question. It names WHAT was
    // reloaded on purpose: when a filter or permission had been fixed on
    // the platform, "conexão atualizada" alone did not tell the user
    // their own access had been re-read, so nobody knew this was the
    // recovery path.
    this.store.addBubble({
      role: 'assistant',
      content:
        'Pronto — recarreguei o modelo do relatório e também os seus '
        + 'filtros e permissões. Pode enviar sua pergunta novamente.',
    });
    headerEl?.markRefreshDone?.();
  };

  private onPickReport = (event: Event): void => {
    const report = (event as CustomEvent<LaravelReport>).detail;
    void this.startSessionFor(report.token, report.name);
  };

  private onRetryFromError = (): void => {
    if (this.isFetchMode()) {
      if (this.currentReportId) {
        void this.startSessionFor(this.currentReportId, this.currentReportName);
      } else {
        void this.loadReportsList();
      }
    }
  };

  // -----------------------------------------------------------
  // Chat send (works in both modes once aiApi is set)
  // -----------------------------------------------------------

  private async onSend(event: Event): Promise<void> {
    const message = (event as CustomEvent<string>).detail;
    if (!this.aiApi || !this.reportId) return;
    this.store.addBubble({ role: 'user', content: message });
    this.store.setLoading(true);
    this.prefill = '';
    try {
      const envelope = await this.aiApi.chat(message, this.store.toChatHistory());
      this.store.addBubble({
        role: 'assistant',
        content: envelope.content,
        envelopeType: envelope.type as 'text' | 'chart' | 'table',
        data: envelope.data,
        charts: envelope.charts,
      });
      this.store.setSuggestions(envelope.suggestions ?? []);
    } catch (err) {
      this.handleError(err);
    } finally {
      this.store.setLoading(false);
    }
  }

  private handleError(err: unknown): void {
    if (err instanceof SessionExpiredError) {
      this.dispatchEvent(
        new CustomEvent('sessionExpired', {
          detail: { reason: err.reason },
          bubbles: true,
          composed: true,
        }),
      );
      // Show a terminal error pointing the user at the refresh button,
      // which is the one action that recovers from both TTL expiry and
      // upstream credential invalidation. No silent auto-re-mint: when
      // the backend rejected the session, retrying automatically would
      // either work (TTL) or loop (bad upstream key). Asking the user
      // to click refresh removes the guessing — same outcome either way,
      // one click, no hidden state.
      this.store.addBubble({
        role: 'error',
        content:
          'Não foi possível conectar com a IA. Peça ao administrador '
          + 'do sistema para conferir a configuração. Quando ele '
          + 'finalizar, clique no ícone de atualizar no topo do chat '
          + 'para reconectar.',
      });
      return;
    }
    let content = 'Ocorreu um erro.';
    if (err instanceof RateLimitedError) {
      content = 'Muitas perguntas em pouco tempo. Aguarde um momento.';
    } else if (err instanceof NetworkError) {
      content = 'Erro de conexão. Verifique sua rede.';
    } else if (err instanceof ApiError) {
      content = err.message;
    }
    this.store.addBubble({ role: 'error', content });
  }

  private findReportTokenById(reportId: string): string | null {
    if (this.currentReportId && reportId === this.reportId) {
      return this.currentReportId;
    }
    return this.reports.find((r) => r.report_id === reportId)?.token ?? null;
  }

  // -----------------------------------------------------------
  // Legacy push-mode report selection (kept for dev harness)
  // -----------------------------------------------------------

  private async onLegacyReportSelect(event: Event): Promise<void> {
    const report = (event as CustomEvent<ReportListItem>).detail;
    this.store.clear();
    this.reportId = report.report_id;
    this.workspaceId = report.workspace_id;
    this.datasetId = report.dataset_id;
    this.dispatchEvent(
      new CustomEvent('reportChanged', {
        detail: {
          reportId: report.report_id,
          workspaceId: report.workspace_id,
          datasetId: report.dataset_id,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private onPickSuggestion(event: Event): void {
    this.prefill = (event as CustomEvent<string>).detail;
  }

  // -----------------------------------------------------------
  // Render
  // -----------------------------------------------------------

  render() {
    if (!this.open) {
      // hide-launcher: the host renders its own trigger and calls openChat().
      return this.hideLauncher
        ? html``
        : html`<bia-chat-launcher @open=${this.openPanel}></bia-chat-launcher>`;
    }
    // Bottom-anchor the floating panel for hosts using the app-style
    // integration. The default `.panel` sits at bottom:80px to leave room for
    // the launcher button — but the launcher is NEVER rendered while the panel
    // is open (render() returns one OR the other), so that 80px is always an
    // empty gap and the card appears to hover mid-air ("voando"). Hosts that
    // opted into the app layout (hide-launcher, OR the floating⇄docked
    // dock-toggle / persist-display-mode features) want the panel anchored to
    // the bottom instead. Classic Quasar floating hosts set none of these, so
    // they keep the historical position and are unaffected. Driven by
    // PROPERTIES (not reflected attributes — Angular property bindings may not
    // reflect in time). Docked/max manage their own position.
    const usesAppLayout =
      this.hideLauncher || this.dockToggle || this.persistDisplayMode;
    const flush =
      usesAppLayout && this.displayMode === 'floating' && !this.maximized;
    const panelClass = [
      'panel',
      this.maximized ? 'max' : '',
      flush ? 'flush' : '',
    ].filter(Boolean).join(' ');
    return html`
      <div class="${panelClass}">${this.renderPanelContents()}</div>
    `;
  }

  private renderPanelContents() {
    if (this.isFetchMode()) {
      return this.renderFetchModePanel();
    }
    return this.renderPushModePanel();
  }

  private renderFetchModePanel() {
    const headerName = this.activeReportName || this.currentReportName || '';
    return html`
      <bia-chat-header
        .reports=${[]}
        .selectedId=${null}
        .maximized=${this.maximized}
        .displayMode=${this.displayMode}
        .dockToggle=${this.dockToggle}
        .reportName=${headerName}
        @close=${this.closePanel}
        @togglemax=${this.toggleMaximize}
        @toggledock=${this.toggleDock}
        @refreshschema=${this.onRefreshSchema}
      ></bia-chat-header>
      ${this.renderFetchPhase()}
    `;
  }

  private renderFetchPhase() {
    if (this.phase === 'loading-reports') {
      return html`<bia-loading-screen message="Carregando relatórios..."></bia-loading-screen>`;
    }
    if (this.phase === 'picking-report') {
      return html`
        <bia-report-picker
          .reports=${this.reports}
          @pick=${this.onPickReport}
        ></bia-report-picker>
      `;
    }
    if (this.phase === 'starting-session') {
      return html`<bia-loading-screen message="Iniciando chat..."></bia-loading-screen>`;
    }
    if (this.phase === 'error') {
      return html`
        <bia-loading-screen
          .error=${this.phaseError}
          .retryable=${true}
          @retry=${this.onRetryFromError}
        ></bia-loading-screen>
      `;
    }
    if (this.phase === 'ready') {
      return this.renderChatBody();
    }
    return html`<bia-loading-screen message="Carregando..."></bia-loading-screen>`;
  }

  private renderPushModePanel() {
    const noReport = !this.reportId;
    const selectedId = this.legacyReports.find((r) => r.report_id === this.reportId)?.id ?? null;
    return html`
      <bia-chat-header
        .reports=${this.legacyReports}
        .selectedId=${selectedId}
        .maximized=${this.maximized}
        .displayMode=${this.displayMode}
        .dockToggle=${this.dockToggle}
        @close=${this.closePanel}
        @togglemax=${this.toggleMaximize}
        @toggledock=${this.toggleDock}
        @refreshschema=${this.onRefreshSchema}
        @select=${this.onLegacyReportSelect}
      ></bia-chat-header>
      <bia-chat-messages
        .messages=${this.store.messages}
        .loading=${this.store.isLoading}
        .noReport=${noReport}
      ></bia-chat-messages>
      <div class="suggestions">
        <bia-suggestion-chips
          .suggestions=${[...this.store.suggestions]}
          @pick=${this.onPickSuggestion}
        ></bia-suggestion-chips>
      </div>
      <bia-chat-input
        .disabled=${noReport}
        .prefill=${this.prefill}
        @send=${this.onSend}
      ></bia-chat-input>
    `;
  }

  private renderChatBody() {
    return html`
      <bia-chat-messages
        .messages=${this.store.messages}
        .loading=${this.store.isLoading}
        .noReport=${false}
      ></bia-chat-messages>
      <div class="suggestions">
        <bia-suggestion-chips
          .suggestions=${[...this.store.suggestions]}
          @pick=${this.onPickSuggestion}
        ></bia-suggestion-chips>
      </div>
      <bia-chat-input
        .disabled=${false}
        .prefill=${this.prefill}
        @send=${this.onSend}
      ></bia-chat-input>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bi-explorer-ai-chat': BiExplorerAiChat;
  }
}
